import {
  accountApi,
  adminApi,
  clearToken,
  getToken,
  markRefreshRejected,
  refreshAccountToken,
  restoreEmergencyAdminSession,
  setToken,
} from '@/api/client';
import type { AuthUser } from '@/api/types';
import { setStepUpToken } from '@/lib/stepup';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  /** 写入 token（+ 可选 user），登录成功后调用。 */
  login: (token: string, user?: AuthUser | null) => void;
  /** 旧独立账号应急登录成功后，使用 HttpOnly session cookie 维持会话。 */
  loginWithSession: (user: AuthUser | null) => void;
  /** 清空登录态。 */
  logout: () => void;
  /** 更新当前用户信息（拉取 profile 后调用）。 */
  setUser: (user: AuthUser | null) => void;
}

const AuthContext = createContext<AuthState | null>(null);

const USER_STORAGE_KEY = 'suite.user';
const SESSION_STORAGE_KEY = 'suite.admin.session';

function readStoredUser(): AuthUser | null {
  try {
    const raw = sessionStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

function writeStoredUser(user: AuthUser | null): void {
  try {
    if (user) sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    else sessionStorage.removeItem(USER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [user, setUserState] = useState<AuthUser | null>(() => readStoredUser());
  const [sessionAuthenticated, setSessionAuthenticated] = useState(
    () => sessionStorage.getItem(SESSION_STORAGE_KEY) === '1',
  );
  const [isInitializing, setIsInitializing] = useState(true);

  // 路由守卫判断前先恢复登录：统一账号从 HttpOnly refresh Cookie 换新 access；
  // 应急账号则探测 PostgreSQL-backed session Cookie。这样发布重载或新标签页不会
  // 因 sessionStorage 暂时为空而提前跳回登录页。
  useEffect(() => {
    let alive = true;
    const bootstrap = async () => {
      const existing = getToken();
      if (existing) {
        setToken(existing);
        if (alive) {
          setTokenState(existing);
          setIsInitializing(false);
        }
        return;
      }
      if (sessionStorage.getItem(SESSION_STORAGE_KEY) === '1') {
        if (alive) setIsInitializing(false);
        return;
      }
      const refreshed = await refreshAccountToken();
      if (refreshed) {
        if (alive) setTokenState(refreshed);
      } else if (await restoreEmergencyAdminSession()) {
        if (alive) {
          sessionStorage.setItem(SESSION_STORAGE_KEY, '1');
          setSessionAuthenticated(true);
        }
      }
      if (alive) setIsInitializing(false);
    };
    void bootstrap().catch(() => {
      // Finish initialization without erasing stored identity on a transient outage.
      if (alive) setIsInitializing(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const login = useCallback((nextToken: string, nextUser?: AuthUser | null) => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    setSessionAuthenticated(false);
    setToken(nextToken);
    setTokenState(nextToken);
    if (nextUser !== undefined) {
      writeStoredUser(nextUser ?? null);
      setUserState(nextUser ?? null);
    }
  }, []);

  const loginWithSession = useCallback((nextUser: AuthUser | null) => {
    clearToken();
    setTokenState(null);
    sessionStorage.setItem(SESSION_STORAGE_KEY, '1');
    setSessionAuthenticated(true);
    writeStoredUser(nextUser);
    setUserState(nextUser);
  }, []);

  /**
   * 退出登录要关掉三样东西，少一样人就退不干净：
   *   1. 后台 session cookie（/admin-api/auth/logout）；
   *   2. 账户中心的旋转 refresh cookie（/account-api/logout，必须带凭据）；
   *   3. 内存里的 step-up 令牌——否则下一个人在同一标签页里继承了敏感操作授权。
   * 同时立旗 `suite.admin.refresh.rejected`，挡住 bootstrap 的静默续期。
   */
  const logout = useCallback(() => {
    void adminApi.post('/auth/logout').catch(() => undefined);
    void accountApi.post('/logout', {}, { withCredentials: true }).catch(() => undefined);
    setStepUpToken(null);
    markRefreshRejected();
    clearToken();
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    setSessionAuthenticated(false);
    writeStoredUser(null);
    setTokenState(null);
    setUserState(null);
  }, []);

  const setUser = useCallback((nextUser: AuthUser | null) => {
    writeStoredUser(nextUser);
    setUserState(nextUser);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      token,
      user,
      isAuthenticated: Boolean(token || sessionAuthenticated),
      isInitializing,
      login,
      loginWithSession,
      logout,
      setUser,
    }),
    [token, user, sessionAuthenticated, isInitializing, login, loginWithSession, logout, setUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}
